import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, X } from "lucide-react";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import usePageMeta from "../lib/usePageMeta";
import { searchProducts, notStockedSuggestion } from "../lib/productSearch";

export default function Products() {
  usePageMeta("Research Catalogue", "Browse the full Peptigen Labs research peptide catalogue.");
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [stockFilter, setStockFilter] = useState("all");
  const [query, setQuery] = useState(searchParams.get("q") || "");

  useEffect(() => { setQuery(searchParams.get("q") || ""); }, [searchParams]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const categories = useMemo(() => {
    const set = new Set(products.map((p) => p.category));
    return Array.from(set);
  }, [products]);

  const q = query.trim();
  const matched = q ? new Set(searchProducts(products, q).map((p) => p.sku)) : null;
  const filtered = products.filter((p) => {
    if (filter !== "all" && p.category !== filter) return false;
    if (stockFilter === "in" && p.stock_status === "out_of_stock") return false;
    if (stockFilter === "out" && p.stock_status !== "out_of_stock") return false;
    if (matched && !matched.has(p.sku)) return false;
    return true;
  });
  const alt = q && filtered.length === 0 ? notStockedSuggestion(q) : null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="products-page">
      <div className="mb-10">
        <div className="pg-micro mb-3">// Catalogue</div>
        <h1 className="text-[#F7FAFC] text-4xl lg:text-5xl pg-display" style={{ fontFamily: "'Exo 2'" }}>
          Research Catalogue
        </h1>
        <p className="text-[#9CA8B8] mt-3 max-w-2xl">
          Premium research peptides for UK laboratories and researchers. All items are supplied
          strictly for laboratory research use only.
        </p>
      </div>

      <div className="relative max-w-xl mb-6">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#00C2D6] pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search — e.g. Retatrutide, GHK-Cu, bac water…"
          className="w-full bg-[#0B1220] border border-[#00C2D6]/25 focus:border-[#00C2D6] rounded-sm pl-11 pr-10 py-3 text-sm text-[#F7FAFC] placeholder-[#5B6776] outline-none transition-colors"
          data-testid="product-search-input"
          aria-label="Search products"
        />
        {query && (
          <button
            onClick={() => setQuery("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA8B8] hover:text-[#F7FAFC] transition-colors"
            data-testid="product-search-clear"
            aria-label="Clear search"
          >
            <X size={16} />
          </button>
        )}
      </div>

      <div className="flex flex-col lg:flex-row gap-6 mb-8">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilter("all")}
            className={`px-4 py-2 text-xs pg-mono uppercase tracking-[0.2em] border rounded-sm transition-colors ${
              filter === "all"
                ? "border-[#00C2D6] text-[#00C2D6] bg-[#00C2D6]/10"
                : "border-[#00C2D6]/20 text-[#9CA8B8] hover:border-[#00C2D6]/50"
            }`}
            data-testid="filter-all"
          >
            All
          </button>
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              className={`px-4 py-2 text-xs pg-mono uppercase tracking-[0.2em] border rounded-sm transition-colors ${
                filter === c
                  ? "border-[#00C2D6] text-[#00C2D6] bg-[#00C2D6]/10"
                  : "border-[#00C2D6]/20 text-[#9CA8B8] hover:border-[#00C2D6]/50"
              }`}
              data-testid={`filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              {c.replace(" Research", "")}
            </button>
          ))}
        </div>
        <div className="flex gap-2 lg:ml-auto">
          {[
            ["all", "All Stock"],
            ["in", "In Stock"],
            ["out", "Out of Stock"],
          ].map(([k, l]) => (
            <button
              key={k}
              onClick={() => setStockFilter(k)}
              className={`px-4 py-2 text-xs pg-mono uppercase tracking-[0.2em] border rounded-sm transition-colors ${
                stockFilter === k
                  ? "border-[#A6FF00] text-[#A6FF00] bg-[#A6FF00]/10"
                  : "border-[#A6FF00]/20 text-[#9CA8B8] hover:border-[#A6FF00]/50"
              }`}
              data-testid={`stock-${k}`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {filtered.map((p) => (
          <ProductCard key={p.sku} product={p} />
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-16 text-[#9CA8B8]" data-testid="no-results">
          {alt && (
            <div className="max-w-xl mx-auto mb-6 px-4 py-4 rounded-sm border border-[#A6FF00]/40 bg-[#A6FF00]/[0.06] text-[#F7FAFC] text-sm" data-testid="not-stocked-suggestion">
              We don&rsquo;t stock {alt.asked}. Researchers in that area order <strong className="text-[#A6FF00]">{alt.suggest}</strong> &mdash; multiple sizes, Buy 3 Get 1 Free.{" "}
              <button onClick={() => setQuery(alt.suggest)} className="text-[#00C2D6] underline underline-offset-2">Show {alt.suggest}</button>
            </div>
          )}
          {query
            ? <>No products match &ldquo;{query}&rdquo;. <button onClick={() => setQuery("")} className="text-[#00C2D6] underline underline-offset-2">Clear search</button></>
            : "No products match these filters."}
        </div>
      )}
    </div>
  );
}
